import AwardsSection from "@components/new-ui/AwardsSection";
import ClientLogos from "@components/new-ui/ClientLogos";
import { container_classname } from "@components/new-ui/constants";
import CTASection from "@components/new-ui/CTASection";
import FAQSection, { FAQSectionProps } from "@components/new-ui/FAQSection";
import FeatureInfoCard, { FeatureInfo } from "@components/new-ui/IndustriesSection/FeatureInfoCard";
import IntegrationSection from "@components/new-ui/IntegrationSection";
import NewLayout from "@components/new-ui/Layout";
import ProblemStatementSection from "@components/new-ui/SpotlightSection";
import { zodResolver } from "@hookform/resolvers/zod";
import { windowOpenNewTab, WORK_EMAIL_REGEX } from "@utils/common";
import axios from "axios";
import Image from "next/image";
import { useState } from "react";
import { Controller, useForm, UseFormRegister } from "react-hook-form";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { useSnackbar } from "react-simple-snackbar";
import { ClipLoader } from "react-spinners";
import { z } from "zod";

const webAppUrl = process.env.NEXT_PUBLIC_GB_APP_PUBLIC_URL || "https://app.gallabox.com";
const postURL = "/api/v1/free-zone/register";

const FormInputSchema = z.object({
    inviteName: z.string({ required_error: "Name is required" }).min(3, "Minimum 3 characters is required"),
    inviteEmail: z.string({ required_error: "Email is required" }).min(3, "Provide a valid business email ID").regex(WORK_EMAIL_REGEX, "Provide a valid business email ID"),
    mobile: z.string({ required_error: "Mobile number is required" }).min(1, "Mobile number is required"),
    businessName: z.string({ required_error: "Business name is required" }).min(1, "Business name is required"),
    tradeLicenseNumber: z.string({ required_error: "Provide a valid trade license no" }).min(1, "Provide a valid trade license no"),
});

type FormInputProps = z.infer<typeof FormInputSchema>;

const defaultValues = {
    inviteName: "",
    inviteEmail: "",
    mobile: "",
    businessName: "",
    tradeLicenseNumber: "",
};

const spotlightContent = [
    {
        title: "It’s consumers’ go-to channel for messaging",
        description: "With an open rate of 98% and 3 billion monthly global active users, WhatsApp remains people’s favorite messaging channel.",
        image: {
            source: "https://assets.gallabox.com/gb-home/freezone/messaging.svg",
            alt: "message",
        },
    },
    {
        title: "WhatsApp is no stranger to business communication",
        description: "Businesses are seeing a conversion rate of over 40% on WhatsApp, making the channel a necessity for growing companies.",
        image: {
            source: "https://assets.gallabox.com/gb-home/freezone/suitcase.svg",
            alt: "suitcase",
        },
    },
    {
        title: "The best place to execute marketing campaigns",
        description: "With WhatsApp Business API's broadcast and message templates, you can engage prospects with intuitve campaigns.",
        image: {
            source: "https://assets.gallabox.com/gb-home/freezone/speaker.svg",
            alt: "broken-chain",
        },
    },
];

const FAQData: FAQSectionProps = {
    title: "Frequently asked questions",
    faqItems: [
        {
            question: "What is WhatsApp Business API and how is it different from WhatsApp Business and personal WhatsApp?",
            answer: (
                <>
                    <p>
                        WhatsApp has three versions. Personal WhatsApp for everyday messaging; WhatsApp Business for small businesses with basic features like auto-replies; and
                        WhatsApp Business API for scaling SMBs and enterprises that need automations, integrations, chatbots, broadcast features, and more.
                    </p>{" "}
                    <br />
                    <p>
                        WhatsApp Business APIs like Gallabox are official Meta partners, providing advanced features like multi-agent support, AI chatbots, shared team inbox,
                        payments, and CRM integrations, to name a few.
                    </p>
                </>
            ),
        },
        {
            question: "What are the key requirements for signing up with Gallabox?",
            answer: (
                <>
                    <p>To set up your WhatsApp Business account with Gallabox, you need the following:</p>
                    <ul>
                        <li>
                            A WhatsApp Business account, which is different from your personal WhatsApp. Find out how to create one{" "}
                            <a href="https://gallabox.com/blog/whatsapp-business-account" target="_blank" rel="noreferrer" className="underline" style={{ color: "#3364FA" }}>
                                here
                            </a>
                            .
                        </li>
                        <li>A mobile number, which is not a part of your WhatsApp Business account. Gallabox will use this to authenticate you.</li>
                        <li>A working business website that’s live.</li>
                    </ul>
                </>
            ),
        },
        {
            question: "If I’m already a Gallabox customer, am I eligible for this SPC Free Zone offer?",
            answer: "No. This offer is valid only for new customers, who run their business in the Sharjah Publishing City (SPC) Free Zone.",
        },
        {
            question: "Do we need to pay for additional features?",
            answer: (
                <p>
                    Any functionality that’s not a part of our Starter plan (as highlighted in the “What your SPC Free Zone business gets from Gallabox” section) will be
                    chargeable. With the{" "}
                    <a href="https://gallabox.com/pricing" target="_blank" rel="noreferrer" className="underline" style={{ color: "#3364FA" }}>
                        upgraded plans
                    </a>
                    ,you will get access to AI-powered chatbots that will elevate business-user messaging to the next level.
                </p>
            ),
        },
        {
            question: "How many users or seats are allowed in the plan?",
            answer: "A maximum of 3 users can manage your WhatsApp Business account using Gallabox.",
        },
        {
            question: "Does the plan include chatbots.",
            answer: "No. The plan doesn’t include any chatbot. However, you can configure auto-response for your broadcast campaigns on WhatsApp. Also, Gallabox has quick/canned responses for common scenarios, to help you reply instantly.",
        },
        {
            question: "Does Gallabox let me send targeted campaigns to contacts?",
            answer: "Yes. This SPC Free Zone plan lets you segment contacts. This means you can set up conditions and filter out contacts to launch personalized broadcast campaigns on WhatsApp.",
        },
    ],
};

const formInfoLists = [
    "This offer is valid only for new customers of Gallabox.",
    "The trade license number should be registered under SPC Free Zone to avail the offer.",
    "Only the features provided under Gallabox’s Starter plan can be availed for free.",
];

const featureInfo1: FeatureInfo = {
    mainTitle: "What your SPC Free Zone business gets from Gallabox",
    imageUrl: "https://assets.gallabox.com/gb-home/freezone/freezone-features.png",
    featureInfoList: [
        "Broadcast and auto-response configuration",
        "Broadcast using Excel sheets",
        "30+ Integrations",
        "Segmentation of contacts",
        "Shared team inbox with collaborative features",
        "Single WhatsApp number/channel",
        "Quick/canned replies",
        "500+ WhatsApp message templates",
        "Basic messaging API",
    ],
};

const FreeZone: React.FC = () => {
    const [formSubmissionLoading, setFormSubmissionLoading] = useState(false);
    const [openSnackbar] = useSnackbar({
        style: {
            backgroundColor: "#DC2626",
        },
    });

    const {
        register,
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<FormInputProps>({
        defaultValues,
        resolver: zodResolver(FormInputSchema),
    });

    const onSubmit = async (formData: FormInputProps) => {
        const { tradeLicenseNumber, ...rest } = formData;

        const payload = {
            freeZone: {
                tradeLicenseNumber: tradeLicenseNumber,
                region: "UAE",
            },
            ...rest,
        };
        try {
            setFormSubmissionLoading(true);
            const response = await axios.post(postURL, payload);
            if (response.status === 200) {
                windowOpenNewTab(`${webAppUrl}/verifyEmail?inviteEmail=${response?.data?.inviteEmail}`);
            }
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = error?.response?.data?.error?.[0] || error?.response?.data?.message || "Unknown error occurred";
                openSnackbar(errorMessage, 5000);
            }
        } finally {
            setFormSubmissionLoading(false);
        }
    };

    const scrollToForm = () => {
        const target = document.getElementById("spc-free-zone");

        if (target) {
            target.scrollIntoView({ behavior: "smooth" });
        }
    };
    return (
        <NewLayout
            title=" WhatsApp Automation Offer for SPC Free Zone"
            description="If your business is a part of the SPC (Sharjah Publishing City) Free Zone, go ahead and claim free WhatsApp automation with Gallabox."
            isFreezone={true}
        >
            <section className="lg:pt-16 pt-10">
                <header className="lg:max-w-7xl lg:mx-auto px-5 w-full flex flex-col lg:py-16 pb-16 gap-16">
                    <div className="flex flex-col lg:flex-row lg:py-5 lg:gap-0 gap-8">
                        <div className="lg:w-1/2 w-full flex flex-col justify-start lg:gap-0 gap-4">
                            <p className="uppercase text-blue-color font-semibold">SPC FREE ZONE OFFER</p>
                            <h1 className="lg:text-new-h1 text-5xl font-bold lg:mb-6" style={{ fontFamily: "Degular Display" }}>
                                Free WhatsApp automations for SPC Free Zone
                            </h1>
                            <p className="text-xl font-normal lg:mb-8 leading-8 lg:w-11/12">
                                If your business belongs to UAE&apos;s SPC Free Zone, you can use Gallabox on a free plan for a year. All you need to pay for is Meta&apos;s
                                conversation charges on WhatsApp.
                            </p>

                            <button
                                onClick={scrollToForm}
                                className="
                                      px-4 py-2 text-white bg-blue-500 rounded-md hover:no-underline focus:outline-none hover:shadow-2xl text-base font-medium flex items-center w-fit
                                "
                            >
                                Apply now
                            </button>
                        </div>
                        <div className="lg:w-1/2 w-full flex lg:justify-end justify-center">
                            <div className="rounded-lg overflow-hidden w-full">
                                <Image
                                    src={"https://assets.gallabox.com/gb-home/freezone/spcfreezone-hero-image.png"}
                                    alt=""
                                    width={505}
                                    height={516}
                                    className="object-cover w-full"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="w-full">
                        <h3 className="text-base text-center font-normal py-2">Trusted by 10,000+ businesses</h3>
                        <ClientLogos />
                    </div>
                </header>
            </section>

            <ProblemStatementSection
                headerText="Why your business should be on WhatsApp"
                data={spotlightContent}
                textClassName={"text-base md:text-xl"}
                sectionStyle={{ fontFamily: "Inter" }}
            />
            <FeatureInfoCard {...featureInfo1} removeDivider imageClassName="object-contain object-top" />

            <div className="md:bg-alice-blue py-20">
                <div className={`${container_classname} flex flex-col md:flex-row gap-20`} style={{ fontFamily: "Inter" }}>
                    <div style={{ maxWidth: "630px" }}>
                        <h3 className="text-4xl md:text-6xl" style={{ fontFamily: "Degular Display" }}>
                            How to apply for your SPC Free Zone privilege on Gallabox
                        </h3>
                        <div style={{ maxWidth: "530px" }}>
                            <p className="text-xl mt-10">
                                Fill in your business details in the form to get started with Gallabox. Once you hit submit, you will receive an email with instructions about next
                                steps.
                            </p>

                            <p className="text-xl mt-5">
                                <strong>Note:</strong> You need to pay Meta for{" "}
                                <a href="https://docs.gallabox.com/pricing-and-billing-modules/conversation-pricing" target="_blank" rel="noreferrer" style={{ color: "#3364FA" }}>
                                    conversations charges on WhatsApp.
                                </a>{" "}
                                Gallabox will not charge you for our features and usage of our WhatsApp automation platform.
                            </p>
                            <p className="text-xl font-semibold mt-10 mb-5">Basic housekeeping</p>
                            {formInfoLists.map((formInfoList, idx) => {
                                return (
                                    <div key={idx} className="flex gap-3">
                                        <div className="flex-shrink-0 mt-3">
                                            <Image src={"https://assets.gallabox.com/gb-home/industry-logos/education/tick-circle.png"} width={24} height={24} alt="tick-circle" />
                                        </div>
                                        <li className="list-none lg:text-xl text-base text-gray-900">{formInfoList}</li>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="py-8 bg-white border border-gray-200 border-solid max-w-xl p-8 rounded-md shadow-md" id="spc-form">
                        <h5 className="text-4xl font-semibold mb-8">Share your business details</h5>
                        <form id="spc-free-zone" onSubmit={handleSubmit(onSubmit)}>
                            <InputField id="inviteName" title="Name" type="text" placeholder="Enter your name" register={register} errorMessage={errors.inviteName?.message} />

                            <InputField
                                id="inviteEmail"
                                title="Business email"
                                type="email"
                                placeholder="Enter your email"
                                register={register}
                                errorMessage={errors.inviteEmail?.message}
                            />

                            <section className="mb-8">
                                <p className="text-gray-800 text-base font-medium mb-1">Mobile number</p>
                                <Controller
                                    control={control}
                                    name="mobile"
                                    render={({ field: { onChange, value } }) => (
                                        <>
                                            <PhoneInput
                                                value={value}
                                                onChange={onChange}
                                                placeholder="Enter your mobile number"
                                                defaultCountry="IN"
                                                countryCallingCodeEditable={false}
                                                international={true}
                                            />
                                            {errors.mobile && <p className="text-red-500 text-xs mt-1">{errors.mobile.message}</p>}
                                        </>
                                    )}
                                />
                            </section>

                            <InputField
                                id="tradeLicenseNumber"
                                title="Trade license number"
                                type="text"
                                placeholder="Enter your Free Zone license number"
                                register={register}
                                errorMessage={errors.tradeLicenseNumber?.message}
                            />

                            <InputField
                                id="businessName"
                                title="Business name"
                                type="text"
                                placeholder="Enter your organization name"
                                register={register}
                                errorMessage={errors.businessName?.message}
                            />

                            <p className="text-xs mt-8">
                                We respect your data. By submitting this form, you agree that we will contact you in relation to our products and services, in accordance with with
                                Gallabox&apos;s Privacy Policy and Terms and Conditions.
                            </p>

                            <button className="bg-blue-600 text-xl font-bold text-white py-4 px-10 text-center rounded-md mt-5 w-full" tabIndex={0} type="submit">
                                {formSubmissionLoading ? <ClipLoader color={"#ffffff"} loading={true} size={20} /> : "Submit"}
                            </button>
                        </form>
                    </div>
                </div>
            </div>

            <FAQSection faqData={FAQData} />
            <IntegrationSection isLight />
            <AwardsSection />
            <CTASection
                title="Scale your SPC Free Zone business on WhatsApp with Gallabox’s automations"
                description="Start your 7-day free trial now!"
                customCTAButton={
                    <button
                        onClick={scrollToForm}
                        className="
                                      px-4 py-2 text-white bg-blue-500 rounded-md hover:no-underline focus:outline-none hover:shadow-2xl text-base font-medium flex items-center w-fit
                                "
                    >
                        Apply now
                    </button>
                }
            />
        </NewLayout>
    );
};

export default FreeZone;

interface InputFieldProps {
    id: keyof FormInputProps;
    title: string;
    type: string;
    placeholder: string;
    register: UseFormRegister<FormInputProps>;
    errorMessage?: string;
}

export const InputField: React.FC<InputFieldProps> = ({ id, title, type, placeholder, register, errorMessage }) => {
    return (
        <section className="flex flex-col gap-1 mb-2">
            <label htmlFor={id} className="text-gray-800 text-base font-medium">
                {title}
            </label>
            <input id={id} type={type} placeholder={placeholder} className="free-zone" {...register(id)} />
            {errorMessage && <p className="text-red-500 text-xs mt-1">{errorMessage}</p>}
        </section>
    );
};
